import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, Copy, Check } from "lucide-react";
import type { LazyErrorType } from "@/components/layout/LazyRoute";

interface Props {
  error: Error;
  errorId: string;
  errorType: LazyErrorType;
  route?: string;
  attempt: number;
}

/**
 * Short, URL-safe error ID. Uses crypto.randomUUID when available and falls
 * back to a Math.random-based string. The visible form is the first 8 chars
 * (case-insensitive) — easy to read out loud in a bug report — while the full
 * value lives in the copy-to-clipboard payload for correlation with logs.
 */
export function makeErrorId(): string {
  const c = (typeof crypto !== "undefined" ? crypto : undefined) as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, "").slice(0, 12);
  return Math.random().toString(36).slice(2, 14);
}

/**
 * Tries to recover the offending dynamic-import URL from a chunk-load
 * error message ("Failed to fetch dynamically imported module: https://…").
 * Returns null when no URL is present.
 */
function extractImportUrl(message: string): string | null {
  const m = message.match(/https?:\/\/\S+\.[mc]?jsx?/i);
  return m?.[0] ?? null;
}

export function ErrorDetails({ error, errorId, errorType, route, attempt }: Props) {
  const [copied, setCopied] = useState(false);
  const importUrl = useMemo(() => extractImportUrl(error.message ?? ""), [error.message]);

  const report = useMemo(
    () =>
      [
        `Error ID: ${errorId}`,
        `When: ${new Date().toISOString()}`,
        `Route: ${route ?? "unknown"}`,
        `Type: ${errorType}`,
        `Attempt: ${attempt}`,
        `Name: ${error.name}`,
        `Message: ${error.message || String(error)}`,
        importUrl ? `Failing import: ${importUrl}` : null,
        `User-Agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "n/a"}`,
        `URL: ${typeof window !== "undefined" ? window.location.href : "n/a"}`,
        "",
        "Stack:",
        error.stack ?? "(no stack)",
      ]
        .filter(Boolean)
        .join("\n"),
    [error, errorId, errorType, route, attempt, importUrl],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked — silently no-op */
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          Error ID:{" "}
          <code className="font-mono text-foreground">{errorId}</code>
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={copy}
          className="h-7 gap-1.5 px-2 text-xs"
          aria-label="Copy error report to clipboard"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy report"}
        </Button>
      </div>
      <details className="group rounded-md border border-border/60 bg-muted/30">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-medium text-foreground/80 hover:text-foreground">
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
          Show technical details
        </summary>
        <div className="space-y-2 border-t border-border/60 p-3">
          {importUrl && (
            <div className="text-xs">
              <span className="text-muted-foreground">Failing import: </span>
              <code className="font-mono break-all text-foreground">{importUrl}</code>
            </div>
          )}
          <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-background/60 p-2 text-[11px] leading-relaxed text-muted-foreground">
            {error.stack ?? error.message ?? String(error)}
          </pre>
        </div>
      </details>
    </div>
  );
}
